import {
  CircularProgress, Container, Grid, SvgIcon, Typography, useMediaQuery,
  useTheme,
} from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { displayChildAge, displayFacilityType, displayFamilyType } from '../../modules/data';
import states from '../../modules/states';
import { toCurrency, toPercent } from '../../modules/text';
import MessageBox from '../MessageBox';
import povertyIncome, { getRatesByState } from './data';

function StateStatsPoverty() {
  const { stateCode } = useParams();
  const theme = useTheme();
  const matchesSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [ready, setReady] = useState(false);
  const {
    familyType, settingType, childAge,
  } = useContext(FiltersContext);
  const { primaryColor, secondaryColor } = useContext(StoryContext);
  const [error, setError] = useState(null);
  const [monthlyPrice, setMonthlyPrice] = useState(null);
  const [averageIncome, setAverageIncome] = useState(0);

  useEffect(() => {
    setReady(false);
    setError(null);

    // function getRatesByState() {
    //   let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
    //   whereClause += composeWhereClause({ settingType, childAge });

    //   return rdsTabulate('ccdc', 'county_setting_group_rate_annual', {
    //     dims: 'state',
    //     measure: 'average_rate:AVG(rate)',
    //     where: whereClause,
    //   }, true);
    // }

    Promise.all([getRatesByState(stateCode, null, { settingType, childAge })])
      .then((responses) => {
        setMonthlyPrice(responses[0].parsedBody.dataProvider[0].average_rate / 12);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setReady(true);
      });
  }, [settingType, childAge, stateCode]);

  useEffect(() => {
    // there are only two possible types
    setAverageIncome(povertyIncome[familyType] / 12);
  }, [familyType]);

  function displayFamilyIncome() {
    return toCurrency(povertyIncome[familyType]);
  }

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <Grid container spacing={2} justify="center">
      <Grid item xs={12} md={8}>
        {error && <Typography variant="body1" align="center" color="error">{error.message}</Typography>}
        <MessageBox>
          <Typography variant="body1">
            In <b>{states[stateCode.toUpperCase()]}</b>,
            the average monthly price of
            full-time child care is <b>{monthlyPrice ? toCurrency(monthlyPrice) : 'N/A'}</b> per <b>{displayChildAge(childAge).toLowerCase()}</b>&nbsp;
            in <b>{displayFacilityType(settingType).toLowerCase().replace('fcc', 'FCC')}</b>.
            That’s <b>{toPercent(monthlyPrice / averageIncome)}</b> of the household income for a <b>{displayFamilyType(familyType).toLowerCase()}</b> earning <b>{displayFamilyIncome()}</b>
            &nbsp;&ndash;&nbsp;100% of FPL.
          </Typography>
        </MessageBox>
      </Grid>
      <Grid item xs={12}>
        <Container style={{ textAlign: 'center' }}>
          <SvgIcon style={{ fontSize: matchesSmall ? '20em' : '29em', marginLeft: '-20px', marginBottom: '-160px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 559.51 464.35">
              <defs>
                <linearGradient id="accessGradientPoverty" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={primaryColor} />
                  <stop offset={`${(monthlyPrice / averageIncome) * 100}%`} stopColor={primaryColor} />
                  <stop offset={`${(monthlyPrice / averageIncome) * 100 + 0.1}%`} stopColor={secondaryColor} />
                  <stop offset="100%" stopColor={secondaryColor} />
                </linearGradient>
              </defs>
              <g fill="url('#accessGradientPoverty')">
                <path d="M550.16,228H41.83V19.41H550.16ZM48.83,221H543.16V26.41H48.83Z" /><path d="M330.1,129.33q-6.18-6.86-22.25-13l-6.43-2.46V83.05a90.35,90.35,0,0,1,26.39,6.26L334,73.82a93.16,93.16,0,0,0-32.57-6.93v-14H290.16V67.14q-15.57,1.6-24.66,9.14a23.82,23.82,0,0,0-9.1,19.2q0,10.92,6.39,18.32t20.26,12.73l7.11,2.63V160.8a76.41,76.41,0,0,1-17.43-2.5,85.45,85.45,0,0,1-17-6v17.85q13.44,6.09,34.43,6.43v17.85h11.26V176.11q16.92-1.86,25.89-9.81a25.85,25.85,0,0,0,9-20.22A24.13,24.13,0,0,0,330.1,129.33ZM290.16,109.7q-7.86-3.14-10.87-6.35a11.32,11.32,0,0,1-3-8,10,10,0,0,1,3.55-7.78q3.56-3.14,10.32-4.15Zm11.26,50.42v-27c5.35,2,9.18,4,11.46,6.14a10.54,10.54,0,0,1,3.43,8.08Q316.31,157.84,301.42,160.12Z" /><path d="M497.08,30.69H94.92A39.78,39.78,0,0,1,55.14,70.47V176.93a39.78,39.78,0,0,1,39.78,39.78H497.08a39.78,39.78,0,0,1,39.78-39.78V70.47A39.78,39.78,0,0,1,497.08,30.69ZM144.56,146.17A22.59,22.59,0,1,1,164.72,126,22.59,22.59,0,0,1,144.56,146.17ZM296,210.53c-43.18,0-78.18-38.88-78.18-86.83s35-86.83,78.18-86.83,78.18,38.88,78.18,86.83S339.18,210.53,296,210.53Zm156-64.36A22.59,22.59,0,1,1,472.2,126,22.59,22.59,0,0,1,452,146.17Z" />

              </g>
            </svg>
          </SvgIcon>
        </Container>
      </Grid>

    </Grid>
  );
}

export default StateStatsPoverty;
